import type { ReactNode } from 'react'

import { useCommunityQuery } from '@/src/features/community/services/useCommunityQuery'
import { NOTIFICATION_CATEGORY } from '@/src/features/notification/models/notification.constants'
import type { TBaseNotificationSettings } from '@/src/features/notification/models/notification.types'
import { useNotificationSettingsMutation } from '@/src/features/notification/services/useNotificationSettingsMutation'
import { useAsyncToggle } from '@/src/general/hooks/useAsyncToggle'

import { SettingsCardCommunitiesBase, type TSettingsCardCommunitiesBaseProps } from './SettingsCardCommunitiesBase'

type TSettingsCardCommunitiesProps = Omit<
  TSettingsCardCommunitiesBaseProps,
  'community' | 'publicationByEmail' | 'publicationByPush' | 'announcementByEmail'
> & {
  communityId: number
  communityName: string
  publications: TBaseNotificationSettings
  announcements: TBaseNotificationSettings
  updateParametersToken?: string
}

export const SettingsCardCommunities = ({
  communityId,
  communityName,
  publications,
  announcements,
  updateParametersToken,
  ...otherProps
}: TSettingsCardCommunitiesProps): ReactNode => {
  const { data } = useCommunityQuery(communityId)
  const { mutateAsync: mutatePublications } = useNotificationSettingsMutation(
    NOTIFICATION_CATEGORY.PUBLICATIONS,
    updateParametersToken,
  )
  const { mutateAsync: mutateAnnouncements } = useNotificationSettingsMutation(
    NOTIFICATION_CATEGORY.ANNOUNCEMENTS,
    updateParametersToken,
  )

  const [isPublicationByEmailEnabled, onPublicationByEmailChange, isPublicationByMailDisabled] = useAsyncToggle(
    publications.email,
    (email) => mutatePublications({ [communityId]: { email } }),
  )
  const [isPublicationByPushEnabled, onPublicationByPushChange, isPublicationByPushDisabled] = useAsyncToggle(
    publications.push,
    (push) => mutatePublications({ [communityId]: { push } }),
  )
  const [isAnnouncementByEmailEnabled, onAnnouncementByEmailChange, isAnnouncementByEmailDisabled] = useAsyncToggle(
    announcements.email,
    async (email) => mutateAnnouncements({ [communityId]: { email } }),
  )

  return (
    <SettingsCardCommunitiesBase
      community={{ name: communityName, ...data }}
      isPublicationByEmailEnabled={isPublicationByEmailEnabled}
      isPublicationByPushEnabled={isPublicationByPushEnabled}
      isAnnouncementByEmailEnabled={isAnnouncementByEmailEnabled}
      publicationByEmailDisabled={isPublicationByMailDisabled}
      publicationByPushDisabled={isPublicationByPushDisabled}
      announcementByEmailDisabled={isAnnouncementByEmailDisabled}
      onPublicationByEmailChange={onPublicationByEmailChange}
      onPublicationByPushChange={onPublicationByPushChange}
      onAnnouncementByEmailChange={onAnnouncementByEmailChange}
      {...otherProps}
    />
  )
}
